type CtaLocation =
  | "navbar"
  | "hero"
  | "final-cta"
  | "contact"
  | "footer"
  | (string & {});

type CtaAction = "whatsapp_chat" | "call" | "copy_number" | "order_now" | (string & {});

type CtaClickPayload = {
  action: CtaAction;
  location: CtaLocation;
  label?: string;
  href?: string;
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Fire a lightweight CTA click event. Integrates with GA4 / GTM when
 * present (window.gtag or window.dataLayer), otherwise logs to console
 * so events are visible during development.
 */
export function trackCtaClick(payload: CtaClickPayload) {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "cta_click", payload);
    } else if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: "cta_click", ...payload });
    }
    window.dispatchEvent(new CustomEvent("cta_click", { detail: payload }));
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[cta_click]", payload);
    }
  } catch {
    /* noop */
  }
}

/**
 * Fire a generic named analytics event. Mirrors trackCtaClick's transport
 * (gtag / dataLayer / CustomEvent). Never pass PII in the payload.
 */
export function trackEvent(name: string, payload: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", name, payload);
    } else if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...payload });
    }
    window.dispatchEvent(new CustomEvent(name, { detail: payload }));
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug(`[${name}]`, payload);
    }
  } catch {
    /* noop */
  }
}